import { useMemo, useState } from 'react';
import { useV2 } from './ctx';
import { Sheet } from './ui';
import { rp, rpS, shortDate, amtOf, sgnOf, todayIso, buildModel } from './model';
import { guessFromBank } from './guess';

// Clicking a bank-only (hatched) row: before adding a new line, look across
// the whole month for unpaid lines this payment probably belongs to
// (Director, 2 Oct: "#2" — a UJ planned on the 2nd and paid on the 6th must
// not become a duplicate line). Only suggestions; nothing is recorded until
// Aurel picks one. "Bukan, tambah baris baru" stays as the fallback.

const STOP = new Set(['dari', 'untuk', 'bayar', 'terima', 'adv', 'dan', 'yang', 'bulan', 'punya', 'pembayaran', 'biaya', 'pak', 'farman']);
const wordsOf = s => (String(s || '').toLowerCase().match(/[a-z]{4,}/g) || []).filter(w => !STOP.has(w));
const trucksOf = s => String(s || '').match(/\b9\d{3}\b/g) || [];

// Several unpaid lines adding up exactly to the entry (a bundled payment).
function subsetSum(items, target, maxSize = 6) {
  let found = null, steps = 0;
  const dfs = (start, sum, picked) => {
    if (found || ++steps > 50000) return;
    if (picked.length >= 2 && Math.abs(sum - target) <= 10) { found = picked.slice(); return; }
    if (picked.length >= maxSize) return;
    for (let i = start; i < items.length; i++) {
      const next = sum + items[i].amt;
      if (next > target + 10) continue;
      picked.push(items[i]); dfs(i + 1, next, picked); picked.pop();
      if (found) return;
    }
  };
  dfs(0, 0, []);
  return found;
}

export function candidatesFor(entry, model) {
  const text = String(entry.description || '').toLowerCase();
  const trucks = trucksOf(entry.description);
  const words = new Set(wordsOf(entry.description));
  const target = Math.abs(entry.amount);
  const pool = model.lines.filter(l => l.status === 'upcoming' && !model.isPendCancel(l) && sgnOf(l) === Math.sign(entry.amount))
    .map(l => {
      const amt = amtOf(l);
      const lt = trucksOf(l.keterangan).concat(/^9\d{3}$/.test(String(l.no_truk || '')) ? [String(l.no_truk)] : []);
      const truckHit = lt.some(t => trucks.includes(t));
      const truckClash = lt.length && trucks.length && !truckHit;
      const shared = wordsOf(l.keterangan).filter(w => words.has(w) || text.includes(w)).length;
      return { line: l, amt, truckHit, truckClash, shared, days: Math.round((Date.parse(entry.tanggal) - Date.parse(model.confirmedDate(l))) / 864e5) };
    })
    .filter(c => !c.truckClash);
  // single lines: exact amount, or the same truck/vendor with a close amount
  const singles = pool.filter(c => c.amt === target || ((c.truckHit || c.shared >= 2) && Math.abs(c.amt - target) <= 0.25 * target))
    .map(c => ({ ...c, score: (c.amt === target ? 3 : 0) + (c.truckHit ? 3 : 0) + c.shared - Math.min(Math.abs(c.days), 10) * 0.1 }))
    .sort((a, b) => b.score - a.score).slice(0, 4);
  // bundle: lines named in the entry (truck or words) adding up exactly
  const related = pool.filter(c => (c.truckHit || c.shared >= 1) && c.amt < target).sort((a, b) => (b.truckHit - a.truckHit) || (b.shared - a.shared));
  const bundle = related.length >= 2 ? subsetSum(related, target) : null;
  return { singles, bundle };
}

export function BankEntrySheet({ entry, onClose }) {
  const { model: bcaModel, data, api, user, monthKey, planId, open, done, legacy } = useV2();
  // An entry from the OCBC account (Buku Kas › Semua / OCBC) is matched
  // against OCBC lines and a new line starts on OCBC (30 Oct: otherwise
  // "Tambah baris baru" defaulted to BCA and the gap moved banks).
  const rek = entry.rek === 'OCBC' ? 'OCBC' : 'BCA';
  const model = useMemo(() => (rek === 'OCBC' ? buildModel(data, 'OCBC') : bcaModel), [rek, data, bcaModel]);
  const { singles, bundle } = useMemo(() => candidatesFor(entry, model), [entry, model]);
  const [busy, setBusy] = useState(false);
  const target = Math.abs(entry.amount), isIn = entry.amount > 0;
  const recordBundle = async () => {
    setBusy(true);
    const errs = [];
    for (const c of bundle) {
      const l = c.line;
      try { await api.createRequest({ monthKey, planId, planLineId: l.id, keterangan: l.keterangan, tipe: l.tipe, customer: l.customer, noTruk: l.no_truk || '', amount: c.amt, requestedBy: user.name, requestDate: entry.tanggal || todayIso() }); }
      catch (e) { errs.push(`${l.keterangan}: ${e.message}`); }
    }
    if (errs.length) alert('Sebagian gagal:\n' + errs.join('\n'));
    await done(`${bundle.length - errs.length} baris dicatat sesuai bank`);
    setBusy(false);
  };
  const late = d => (d > 0 ? `dibayar ${d} hari setelah rencana` : d < 0 ? `dibayar ${-d} hari sebelum rencana` : 'tanggal sama');
  return (
    <Sheet title={`${isIn ? 'Uang masuk' : 'Uang keluar'} di bank`} onClose={onClose}>
      <div className="note" style={{ fontSize: 12.5, display: 'grid', gap: 2 }}>
        <b><span className={`rektag ${rek.toLowerCase()}`}>{rek}</span>{shortDate(entry.tanggal)} · {rp(target)}</b>
        <span className="muted">{entry.description}</span>
      </div>
      {!singles.length && !bundle && <p className="hint">Tidak ada baris di plan bulan ini yang cocok. Tambah sebagai baris baru.</p>}
      {(singles.length > 0 || bundle) && <div className="lbl" style={{ padding: '4px 0 0' }}><span>Mungkin ini baris di plan</span><span>belum dibayar</span></div>}
      {bundle && (
        <div className="group" style={{ padding: '10px 12px', display: 'grid', gap: 6 }}>
          <b style={{ fontSize: 13.5 }}>{bundle.length} baris, jumlahnya pas {rp(target)}</b>
          {bundle.map(c => <div key={c.line.id} className="bridge-r" style={{ color: 'var(--text)' }}><span>{c.line.keterangan} <span className="muted">· rencana {shortDate(model.confirmedDate(c.line))}</span></span><span className="num">{rp(c.amt)}</span></div>)}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button type="button" className="btn small" disabled={busy} onClick={recordBundle}>{busy ? 'Mencatat…' : `Catat ${bundle.length} baris sesuai bank`}</button></div>
        </div>
      )}
      {singles.map(c => {
        const diff = target - c.amt;
        return (
          <div key={c.line.id} className="group" style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div className="t" style={{ fontWeight: 600 }}>{c.line.keterangan}</div>
              <div className="m">rencana {shortDate(model.confirmedDate(c.line))} · {rp(c.amt)} · {late(c.days)}{diff ? <span className={diff > 0 ? 'bad' : 'pos'}> · bank {rpS(diff)}</span> : ''}</div>
            </div>
            <button type="button" className="btn small" style={{ flexShrink: 0 }} onClick={() => open('catat', { line: c.line, presetDate: entry.tanggal, presetAmount: target })}>Catat sesuai bank</button>
          </div>
        );
      })}
      <button type="button" className="btn ghost" onClick={() => open('tambah', { preset: { ...guessFromBank(entry, legacy), rekening: rek } })}>{singles.length || bundle ? 'Bukan, tambah baris baru' : 'Tambah baris baru'}</button>
      {(singles.length > 0 || bundle) && <p className="hint" style={{ margin: 0 }}>Jumlah berbeda dari plan tetap menunggu persetujuan Direktur.</p>}
    </Sheet>
  );
}
